import { Box, Button, IconButton, MenuItem, TextField, Tooltip, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import ArrowDownwardRoundedIcon from '@mui/icons-material/ArrowDownwardRounded';
import ArrowUpwardRoundedIcon from '@mui/icons-material/ArrowUpwardRounded';
import ExpandLessRoundedIcon from '@mui/icons-material/ExpandLessRounded';
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import moment from 'moment-timezone';
import React from 'react';
import {
    Bar,
    BarChart,
    CartesianGrid,
    LabelList,
    Line,
    LineChart,
    ResponsiveContainer,
    Tooltip as ChartTooltip,
    XAxis,
    YAxis,
} from 'recharts';

import { DataAPIContext, GET_LIST } from '../api';
import { BY_DAY_MAX_DAYS, byDayRangeAllowed } from './agentMetricsByDay';
import {
    COMPARE_METRICS,
    DEFAULT_COMPARE_METRIC,
    MEDIAN_MIN_REPLIES,
    TREND_SERIES,
    dailyTotals,
    formatCompact,
    formatCount,
    formatDayRange,
    formatDuration,
    niceTicks,
    percentChange,
    previousPeriod,
    rankAgents,
    reportRange,
    summarize,
} from './agentMetricsTotals';

const STORAGE_KEY_OPEN = 'AgentMetricsSummary.open';
const STORAGE_KEY_COMPARE_METRIC = 'AgentMetricsSummary.compareMetric';
// One series: one color for every bar (categorical slot 1 of the chart palette).
const COMPARE_COLOR = '#2a78d6';
const COMPARE_ROW_PX = 28;
// The same number the Ratings charts show before "Show all" (ReviewInsights MAX_BARS_SHOWN).
const COMPARE_TOP = 12;

// Chart chrome and ink (light only: the dashboard outside chat has no dark theme).
const INK = {
    primary: '#0b0b0b',
    secondary: '#52514e',
    muted: '#898781',
    grid: '#e1e0d9',
    axis: '#c3c2b7',
    good: '#006300',
    bad: '#d03b3b',
    border: '#D8D8D8',
};

// Plain divs, not Box: the Agent Metrics page zeroes the padding of every .MuiBox-root
// inside it (AgentMetricsList `root`).
const Card = styled('div')({
    border: `1px solid ${INK.border}`,
    borderRadius: 12,
    background: '#fff',
    padding: '12px 16px',
    minWidth: 0,
});
const TooltipCard = styled('div')({
    background: '#fff',
    border: `1px solid ${INK.border}`,
    borderRadius: 8,
    padding: '8px 12px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
});

/**
 * The width from the element's left edge to the right edge of the window, less `gutter`.
 * The Agent Metrics page is at least 1400px wide (its grid), so on a laptop the window
 * scrolls sideways; the summary keeps to the part that is on screen instead of putting
 * half the chart under the scroll. Re-measured when the window or the page's <main>
 * (the menu opening or closing) changes size.
 */
function useVisibleWidth(ref, gutter, layoutKey) {
    const [width, setWidth] = React.useState(null);
    React.useLayoutEffect(() => {
        const el = ref.current;
        if (!el || typeof window === 'undefined') {
            return undefined;
        }
        const measure = () => {
            // Read the ref each time: showing / hiding the summary swaps the element.
            const node = ref.current;
            if (!node) return;
            const left = node.getBoundingClientRect().left + window.scrollX;
            const available = document.documentElement.clientWidth - left - gutter;
            setWidth(available > 0 ? Math.floor(available) : null);
        };
        measure();
        window.addEventListener('resize', measure);
        const main = el.closest('main');
        const observer = main && typeof ResizeObserver !== 'undefined' ? new ResizeObserver(measure) : null;
        observer?.observe(main);
        return () => {
            window.removeEventListener('resize', measure);
            observer?.disconnect();
        };
    }, [ref, gutter, layoutKey]);
    return width;
}

function readStoredOpen() {
    try {
        return window.localStorage.getItem(STORAGE_KEY_OPEN) !== 'false';
    } catch {
        return true;
    }
}

/** The report for one set of filters: its rows, its meta, and the HTTP status. */
async function fetchReport(dataAPI, filters, query, signal) {
    let meta = null;
    const res = await dataAPI(GET_LIST, 'report/staff', {
        signal,
        convert: (json) => {
            meta = json?.meta || null;
            return json;
        },
        pagination: { page: 1, perPage: 100 },
        sort: { field: 'name', order: 'ASC' },
        filter: { query, filters: JSON.stringify(filters) },
    });
    return { rows: res?.data || [], meta, statusCode: res?.statusCode };
}

/** The page's filters with the date range and by-day switch replaced. */
function withRange(apiFilters, start, end, groupByDay) {
    const replaced = new Set(['start_datetime', 'end_datetime', 'group_by_day']);
    return [
        ...(apiFilters || []).filter((f) => !replaced.has(f.field)),
        { field: 'start_datetime', value: start },
        { field: 'end_datetime', value: end },
        { field: 'group_by_day', value: groupByDay },
    ];
}

const pct = (ratio) => {
    const v = Math.abs(ratio * 100);
    return `${v < 10 ? (Math.round(v * 10) / 10).toString() : Math.round(v)}%`;
};

/**
 * A change worded and colored by whether the direction is good. `lowerIsBetter` null means
 * the direction says nothing about performance (more conversations is more traffic), so it
 * stays in neutral ink.
 */
function deltaFor(change, { lowerIsBetter = null, unit = 'pct' } = {}) {
    if (change === null || change === undefined || Number.isNaN(change)) {
        return null;
    }
    const flat = unit === 'pct' ? Math.abs(change) < 0.005 : Math.abs(change) < 0.05;
    if (flat) {
        return { direction: 'flat', text: 'No change', tone: 'neutral' };
    }
    const up = change > 0;
    const text = unit === 'pct'
        ? pct(change)
        : `${(Math.round(Math.abs(change) * 10) / 10).toString()} pts`;
    let tone = 'neutral';
    if (lowerIsBetter !== null) {
        tone = up === lowerIsBetter ? 'bad' : 'good';
    }
    return { direction: up ? 'up' : 'down', text: `${up ? '+' : '−'}${text}`, tone };
}

const TONE_COLOR = { good: INK.good, bad: INK.bad, neutral: INK.secondary };

function Delta({ delta, against, againstTitle }) {
    const color = TONE_COLOR[delta.tone] || INK.secondary;
    const Icon = delta.direction === 'up' ? ArrowUpwardRoundedIcon : delta.direction === 'down' ? ArrowDownwardRoundedIcon : null;
    return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px', minWidth: 0 }}>
            <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', color, fontSize: 13, fontWeight: 600, whiteSpace: 'nowrap' }}>
                {Icon && <Icon sx={{ fontSize: 15, mr: '1px' }} aria-hidden />}
                {delta.text}
            </Box>
            <Tooltip title={againstTitle || ''} arrow placement="bottom">
                <Typography component="span" sx={{ fontSize: 12, color: INK.muted, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    vs {against}
                </Typography>
            </Tooltip>
        </Box>
    );
}

function StatTile({ label, help, value, caption, delta, note, against, againstTitle }) {
    return (
        <Card data-testid="agent-metrics-stat" style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                <Typography sx={{ fontSize: 13, color: INK.secondary, fontWeight: 500 }}>{label}</Typography>
                <Tooltip title={help} arrow placement="top">
                    <HelpOutlineIcon sx={{ fontSize: 14, color: INK.muted, cursor: 'help' }} />
                </Tooltip>
            </Box>
            <Typography sx={{ fontSize: 28, lineHeight: 1.25, fontWeight: 600, color: INK.primary }}>{value}</Typography>
            {caption !== undefined && (
                <Typography sx={{ fontSize: 12, color: INK.secondary, minHeight: 18 }}>{caption}</Typography>
            )}
            <Box sx={{ minHeight: 20, display: 'flex', alignItems: 'center' }}>
                {delta
                    ? <Delta delta={delta} against={against} againstTitle={againstTitle} />
                    : <Typography sx={{ fontSize: 12, color: INK.muted }}>{note}</Typography>}
            </Box>
        </Card>
    );
}

function readStoredCompareMetric() {
    try {
        const stored = window.localStorage.getItem(STORAGE_KEY_COMPARE_METRIC);
        if (COMPARE_METRICS.some((m) => m.key === stored)) return stored;
    } catch {
        /* quota / privacy mode */
    }
    return DEFAULT_COMPARE_METRIC;
}

/** An agent's name beside its bar: shortened with an ellipsis, the whole name on hover. */
function AgentNameTick({ x, y, payload }) {
    const name = String(payload?.value ?? '');
    const short = name.length > 24 ? `${name.slice(0, 23)}…` : name;
    return (
        <g transform={`translate(${x},${y})`}>
            <title>{name}</title>
            <text x={-8} y={0} dy={4} textAnchor="end" fontSize={12} fill={INK.secondary}>{short}</text>
        </g>
    );
}

function ComparisonTooltip({ active, payload, metric }) {
    if (!active || !payload || !payload.length) {
        return null;
    }
    const agent = payload[0].payload;
    return (
        <TooltipCard>
            <Typography sx={{ fontSize: 12, color: INK.secondary, mb: '2px' }}>{agent.name}</Typography>
            <Typography sx={{ fontSize: 13, fontWeight: 600, color: INK.primary }}>
                {metric.duration ? formatDuration(agent.value) : formatCount(agent.value)}
                <Typography component="span" sx={{ fontSize: 12, fontWeight: 400, color: INK.secondary, ml: '6px' }}>
                    {metric.label.toLowerCase()}
                </Typography>
            </Typography>
            {metric.duration && (
                <Typography sx={{ fontSize: 12, color: INK.muted }}>{formatCount(agent.replies)} replies to guests</Typography>
            )}
        </TooltipCard>
    );
}

/**
 * The agents side by side on one measure (2026-10-29, from the Figma "Agent performance
 * comparison"): the top 12, best first, and every agent on "Show all N" (as the Ratings charts do),
 * from the grid's own rows -- no request of its own, so it shows with the table. One measure at a
 * time rather than four bars per agent, which stopped fitting past six agents.
 */
function AgentComparison({ rows, byDay, reportLoaded }) {
    const [metricKey, setMetricKey] = React.useState(readStoredCompareMetric);
    const pickMetric = (key) => {
        setMetricKey(key);
        try {
            window.localStorage.setItem(STORAGE_KEY_COMPARE_METRIC, key);
        } catch {
            /* quota / privacy mode */
        }
    };
    const [showAll, setShowAll] = React.useState(false);
    const ranking = React.useMemo(() => rankAgents(rows, metricKey, { byDay, limit: Infinity }), [rows, metricKey, byDay]);
    const { metric, agents: ranked, total } = ranking;
    const agents = showAll ? ranked : ranked.slice(0, COMPARE_TOP);
    const format = metric.duration ? formatDuration : formatCount;

    let caption = '';
    if (agents.length) {
        caption = agents.length < total ? `Top ${agents.length} of ${total} agents` : `All ${total} agent${total === 1 ? '' : 's'}`;
        caption += ` · ${metric.duration ? 'fastest' : 'most'} first`;
        if (metric.duration) caption += ` · agents with ${MEDIAN_MIN_REPLIES} or more replies to guests`;
    }

    let body;
    if (!reportLoaded) {
        body = <Typography sx={{ fontSize: 13, color: INK.muted }}>Loading…</Typography>;
    } else if (metric.duration && byDay) {
        body = <Typography sx={{ fontSize: 13, color: INK.muted }}>Turn off By day to compare response times: a median does not add up across days.</Typography>;
    } else if (!agents.length) {
        body = (
            <Typography sx={{ fontSize: 13, color: INK.muted }}>
                {metric.duration
                    ? `No agent has ${MEDIAN_MIN_REPLIES} or more replies to guests in this range.`
                    : `No agent has any ${metric.label.toLowerCase()} in this range.`}
            </Typography>
        );
    } else {
        body = (
            <Box sx={{ width: '100%' }}>
                <Box sx={{ width: '100%', height: agents.length * COMPARE_ROW_PX + 8 }}>
                    <ResponsiveContainer width="100%" height="100%">
                        <BarChart layout="vertical" data={agents} margin={{ top: 4, right: 64, left: 0, bottom: 4 }} barCategoryGap={6}>
                            <XAxis type="number" hide domain={[0, 'dataMax']} />
                            <YAxis
                                type="category"
                                dataKey="name"
                                width={180}
                                interval={0}
                                tickLine={false}
                                axisLine={{ stroke: INK.axis }}
                                tick={<AgentNameTick />}
                            />
                            <ChartTooltip
                                cursor={{ fill: 'rgba(42, 120, 214, 0.06)' }}
                                content={<ComparisonTooltip metric={metric} />}
                                isAnimationActive={false}
                            />
                            <Bar dataKey="value" fill={COMPARE_COLOR} barSize={16} radius={[0, 4, 4, 0]} isAnimationActive={false}>
                                <LabelList dataKey="value" position="right" formatter={format} style={{ fontSize: 12, fill: INK.secondary }} />
                            </Bar>
                        </BarChart>
                    </ResponsiveContainer>
                </Box>
                {total > COMPARE_TOP && (
                    <Button size="small" onClick={() => setShowAll((v) => !v)} sx={{ mt: '4px' }}>
                        {showAll ? `Show top ${COMPARE_TOP}` : `Show all ${total}`}
                    </Button>
                )}
            </Box>
        );
    }

    return (
        <Card data-testid="agent-metrics-compare">
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '4px 16px', mb: '8px' }}>
                <Box sx={{ display: 'flex', alignItems: 'baseline', gap: '12px', flexWrap: 'wrap' }}>
                    <Typography sx={{ fontSize: 14, fontWeight: 600, color: INK.primary }}>Compare agents</Typography>
                    {caption && <Typography sx={{ fontSize: 12, color: INK.muted }}>{caption}</Typography>}
                </Box>
                <TextField
                    select
                    size="small"
                    value={metric.key}
                    onChange={(e) => pickMetric(e.target.value)}
                    inputProps={{ 'aria-label': 'Measure to compare agents by' }}
                    sx={{ minWidth: 220, '& .MuiSelect-select': { fontSize: 13, py: '6px' } }}
                >
                    {COMPARE_METRICS.map((m) => (
                        <MenuItem key={m.key} value={m.key} sx={{ fontSize: 13 }}>{m.label}</MenuItem>
                    ))}
                </TextField>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', minHeight: 60 }}>{body}</Box>
        </Card>
    );
}

function TrendTooltip({ active, payload, timezone }) {
    if (!active || !payload || !payload.length) {
        return null;
    }
    const point = payload[0].payload;
    return (
        <TooltipCard>
            <Typography sx={{ fontSize: 12, color: INK.secondary, mb: '4px' }}>
                {moment.tz(point.day, 'YYYY-MM-DD', timezone).format('ddd, MMM D')}
                {point.partial ? ' (so far)' : ''}
            </Typography>
            {TREND_SERIES.map((s) => (
                <Box key={s.key} sx={{ display: 'flex', alignItems: 'center', gap: '8px', lineHeight: '20px' }}>
                    <Box component="span" sx={{ width: 12, height: 2, bgcolor: s.color, borderRadius: 1, flex: 'none' }} />
                    <Typography component="span" sx={{ fontSize: 13, fontWeight: 600, color: INK.primary, minWidth: 28, textAlign: 'right' }}>
                        {formatCount(point[s.key])}
                    </Typography>
                    <Typography component="span" sx={{ fontSize: 12, color: INK.secondary }}>{s.label}</Typography>
                </Box>
            ))}
        </TooltipCard>
    );
}

/**
 * Two lines per series: solid over whole days, dashed from yesterday into today while the
 * range runs up to now, so today's part-day total does not read as a drop.
 */
function chartPoints(points) {
    const partialIndex = points.findIndex((p) => p.partial);
    return points.map((p, i) => {
        const out = { ...p };
        TREND_SERIES.forEach((s) => {
            out[`${s.key}__whole`] = p.partial ? null : p[s.key];
            out[`${s.key}__partial`] = partialIndex >= 0 && i >= partialIndex - 1 ? p[s.key] : null;
        });
        return out;
    });
}

function TrendChart({ points, timezone, status, unavailableReason, dim }) {
    const data = React.useMemo(() => chartPoints(points), [points]);
    const ticks = React.useMemo(
        () => niceTicks(Math.max(0, ...points.flatMap((p) => TREND_SERIES.map((s) => p[s.key])))),
        [points],
    );
    const hasPartial = points.some((p) => p.partial);
    const muted = (text) => <Typography sx={{ fontSize: 13, color: INK.muted, maxWidth: 420 }}>{text}</Typography>;

    let body;
    if (unavailableReason) {
        body = muted(unavailableReason);
    } else if (status === 'busy' && !points.length) {
        body = muted('Agent Metrics is busy right now. Refresh in a few seconds to load the trend.');
    } else if (status === 'error' && !points.length) {
        body = muted('The trend could not be loaded. Refresh to try again.');
    } else if (!points.length) {
        body = muted('Loading…');
    } else {
        body = (
            <Box sx={{ width: '100%', height: 200, opacity: dim ? 0.5 : 1, transition: 'opacity 150ms' }}>
                <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={data} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
                        <CartesianGrid vertical={false} stroke={INK.grid} />
                        <XAxis
                            dataKey="day"
                            tickFormatter={(d) => moment.tz(d, 'YYYY-MM-DD', timezone).format('MMM D')}
                            tick={{ fontSize: 11, fill: INK.muted }}
                            axisLine={{ stroke: INK.axis }}
                            tickLine={false}
                            minTickGap={24}
                        />
                        <YAxis
                            domain={[0, ticks[ticks.length - 1]]}
                            ticks={ticks}
                            tickFormatter={formatCompact}
                            tick={{ fontSize: 11, fill: INK.muted }}
                            axisLine={false}
                            tickLine={false}
                            width={40}
                        />
                        <ChartTooltip
                            cursor={{ stroke: INK.axis, strokeWidth: 1 }}
                            content={<TrendTooltip timezone={timezone} />}
                            isAnimationActive={false}
                        />
                        {TREND_SERIES.map((s) => (
                            <Line
                                key={s.key}
                                dataKey={`${s.key}__whole`}
                                name={s.label}
                                stroke={s.color}
                                strokeWidth={2}
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                dot={false}
                                activeDot={{ r: 4, fill: s.color, stroke: '#fff', strokeWidth: 2 }}
                                connectNulls={false}
                                isAnimationActive={false}
                            />
                        ))}
                        {hasPartial && TREND_SERIES.map((s) => (
                            <Line
                                key={`${s.key}-partial`}
                                dataKey={`${s.key}__partial`}
                                name={`${s.label} (today so far)`}
                                stroke={s.color}
                                strokeWidth={2}
                                strokeDasharray="4 4"
                                dot={false}
                                activeDot={{ r: 4, fill: s.color, stroke: '#fff', strokeWidth: 2 }}
                                connectNulls={false}
                                isAnimationActive={false}
                                legendType="none"
                            />
                        ))}
                    </LineChart>
                </ResponsiveContainer>
            </Box>
        );
    }

    return (
        <Card style={{ display: 'flex', flexDirection: 'column', paddingBottom: 8 }} data-testid="agent-metrics-trend">
            <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', flexWrap: 'wrap', gap: '4px 16px', mb: '6px' }}>
                <Typography sx={{ fontSize: 14, fontWeight: 600, color: INK.primary }}>Daily activity</Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '4px 14px' }} data-testid="agent-metrics-trend-legend">
                    {TREND_SERIES.map((s) => (
                        <Box key={s.key} sx={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <Box component="span" sx={{ width: 14, height: 2, bgcolor: s.color, borderRadius: 1 }} />
                            <Typography component="span" sx={{ fontSize: 12, color: INK.secondary }}>{s.label}</Typography>
                        </Box>
                    ))}
                    {hasPartial && !unavailableReason && (
                        <Typography component="span" sx={{ fontSize: 12, color: INK.muted }}>Dashed: today so far</Typography>
                    )}
                </Box>
            </Box>
            <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: points.length && !unavailableReason ? 'stretch' : 'center', minHeight: 200 }}>
                {body}
            </Box>
        </Card>
    );
}

/**
 * Headline numbers and the daily trend above the Agent Metrics grid (2026-10-29).
 *
 * The tiles are the grid's own rows summed, so they always agree with the table. Two more
 * reports run after the page's: the range by day (the chart; skipped when the grid is
 * already by day, whose rows are used instead) and the same filters over the previous
 * period (the changes). They start only once the table's report has answered
 * (`waitForTable`), and then one after the other. Each report reads the same ~230M rows in
 * ClickHouse whatever the range, and side by side they slow each other down: City Wonders'
 * 7-day table took 3.2 s alone and ~6 s with the by-day report beside it, ~9 s with all
 * three (prod, 2026-10-29). Hiding the summary stops both.
 */
const AgentMetricsSummary = ({
    rows,
    reportLoaded,
    medianSeconds,
    avgSeconds,
    apiFilters,
    query,
    startDate,
    endDate,
    timezone,
    byDay,
    refreshKey,
    isSmallScreen,
    waitForTable = false,
}) => {
    const dataAPI = React.useContext(DataAPIContext);
    const [open, setOpen] = React.useState(readStoredOpen);
    const toggleOpen = () => {
        setOpen((was) => {
            try {
                window.localStorage.setItem(STORAGE_KEY_OPEN, String(!was));
            } catch {
                /* quota / privacy mode */
            }
            return !was;
        });
    };

    const tz = timezone || 'UTC';
    // Recomputed on refresh too: a range running up to now compares against the same
    // length of time before it, which moves with the clock.
    const range = React.useMemo(() => reportRange(startDate, endDate, tz), [startDate, endDate, tz, refreshKey]); // eslint-disable-line react-hooks/exhaustive-deps
    const previous = React.useMemo(() => previousPeriod(startDate, endDate, tz), [startDate, endDate, tz, refreshKey]); // eslint-disable-line react-hooks/exhaustive-deps
    const trendFetchNeeded = !byDay && !!range && range.days >= 2 && byDayRangeAllowed(startDate, endDate, tz);

    // Each answer remembers the request it answers (`key`), so a figure for the last range
    // never shows as this range's: until this range's answer is in, the changes say
    // "Comparing…" and the chart keeps the last points, dimmed.
    const [prevState, setPrevState] = React.useState({ status: 'idle', summary: null, key: null });
    const [trendState, setTrendState] = React.useState({ status: 'idle', points: [], key: null });

    const prevKey = previous ? JSON.stringify(withRange(apiFilters, previous.start, previous.end, false)) : '';
    const trendKey = trendFetchNeeded ? JSON.stringify(withRange(apiFilters, startDate, endDate, true)) : '';

    React.useEffect(() => {
        if (!open || !dataAPI || waitForTable) {
            return undefined;
        }
        let active = true;
        const ac = new AbortController();
        const settle = (res) => {
            if (res.statusCode === 429 || res.meta?.busy) return 'busy';
            if (res.statusCode && res.statusCode >= 400) return 'error';
            return 'ready';
        };
        (async () => {
            if (trendKey) {
                setTrendState((s) => ({ ...s, status: 'loading', key: trendKey }));
                const res = await fetchReport(dataAPI, JSON.parse(trendKey), query, ac.signal);
                if (!active) return;
                const status = settle(res);
                setTrendState((s) => ({
                    status,
                    points: status === 'ready' ? dailyTotals(res.rows, startDate, endDate, tz) : s.points,
                    key: trendKey,
                }));
            }
            if (prevKey) {
                setPrevState((s) => ({ ...s, status: 'loading', key: prevKey }));
                const res = await fetchReport(dataAPI, JSON.parse(prevKey), query, ac.signal);
                if (!active) return;
                const status = settle(res);
                setPrevState({
                    status,
                    summary: status === 'ready' ? summarize(res.rows, res.meta?.totals?.median_response_seconds) : null,
                    key: prevKey,
                });
            } else {
                setPrevState({ status: 'idle', summary: null, key: prevKey });
            }
        })().catch((e) => {
            if (e?.name === 'AbortError' || !active) return;
            setTrendState((s) => ({ ...s, status: 'error', key: trendKey }));
            setPrevState({ status: 'error', summary: null, key: prevKey });
        });
        return () => {
            active = false;
            ac.abort();
        };
        // startDate / endDate / tz are inside trendKey; listing them re-runs nothing extra.
    }, [open, dataAPI, waitForTable, trendKey, prevKey, query, refreshKey, startDate, endDate, tz]);

    const current = summarize(rows, medianSeconds);
    const prevForThisRange = prevState.key === prevKey;
    const before = prevForThisRange && prevState.status === 'ready' ? prevState.summary : null;
    const trendForThisRange = trendState.key === trendKey;

    const against = previous ? formatDayRange(previous.first, previous.lastDay, undefined, { compact: true }) : '';
    const againstTitle = previous
        ? (previous.toNow
            ? `Compared with the same length of time just before this range: ${previous.first.format('MMM D, YYYY')} to ${previous.cutoff.format('MMM D, YYYY HH:mm')} (${tz})`
            : `Compared with the ${previous.days} days just before this range: ${formatDayRange(previous.first, previous.lastDay)} (${tz})`)
        : '';
    // What a tile says where it shows no change: why there is none.
    const noteFor = (prevValue) => {
        if (!previous || !reportLoaded) return '';
        if (!prevForThisRange || prevState.status === 'loading') return 'Comparing…';
        if (prevState.status === 'busy') return 'Comparison busy, refresh to retry';
        if (prevState.status === 'error') return 'Comparison unavailable';
        if (before && !prevValue) return `No activity ${against}`;
        return '';
    };

    const shown = (v, fmt = formatCount) => (reportLoaded ? fmt(v) : '—');
    const tileDelta = (value, prevValue, opts) => (reportLoaded && before ? deltaFor(percentChange(value, prevValue), opts) : null);
    const shareDelta = reportLoaded && before && current.noActionShare !== null && before.noActionShare !== null
        ? deltaFor((current.noActionShare - before.noActionShare) * 100, { lowerIsBetter: true, unit: 'pts' })
        : null;
    const tileProps = { against, againstTitle };
    // The tiles and the agent chart are the table's rows: while the table reloads they are the last
    // range's, so they fade until the new rows are in.
    const staleRows = waitForTable && reportLoaded;
    const fadeWhenStale = { opacity: staleRows ? 0.5 : 1, transition: 'opacity 150ms' };

    const trendPoints = React.useMemo(() => {
        if (!range || range.days < 2) return [];
        if (byDay) return reportLoaded ? dailyTotals(rows, startDate, endDate, tz) : [];
        return trendState.points;
    }, [range, byDay, reportLoaded, rows, trendState, startDate, endDate, tz]);

    let unavailableReason = null;
    if (!range) {
        unavailableReason = `The daily trend needs a start date. Pick a range of up to ${BY_DAY_MAX_DAYS} days to see it.`;
    } else if (range.days < 2) {
        unavailableReason = 'Pick a range of two days or more to see the daily trend.';
    } else if (!byDay && !trendFetchNeeded) {
        unavailableReason = `The daily trend covers up to ${BY_DAY_MAX_DAYS} days. Pick a shorter range to see it.`;
    }

    const gutter = isSmallScreen ? 8 : 16;
    const fitRef = React.useRef(null);
    const fitWidth = useVisibleWidth(fitRef, gutter, open);
    const frame = { ml: `${gutter}px`, mr: `${gutter}px`, maxWidth: fitWidth ? `${fitWidth}px` : undefined };
    if (!open) {
        return (
            <Box ref={fitRef} sx={{ ...frame, display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
                <Button size="small" onClick={toggleOpen} endIcon={<ExpandMoreRoundedIcon />} sx={{ textTransform: 'none', color: INK.secondary }}>
                    Show summary
                </Button>
            </Box>
        );
    }

    return (
        <Box ref={fitRef} sx={{ ...frame, mb: 2 }} data-testid="agent-metrics-summary">
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                <Typography sx={{ fontSize: 13, color: INK.secondary }}>
                    {range
                        ? `${formatDayRange(range.first, range.last)}${range.toNow ? ' (to now)' : ''} · ${tz}`
                        : `All time · ${tz}`}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Tooltip title="Hide summary" arrow>
                        <IconButton size="small" onClick={toggleOpen} aria-label="Hide summary">
                            <ExpandLessRoundedIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                </Box>
            </Box>
            <Box
                sx={{
                    display: 'grid',
                    gap: '12px',
                    gridTemplateColumns: isSmallScreen ? '1fr' : 'minmax(460px, 5fr) 7fr',
                    alignItems: 'stretch',
                }}
            >
                <Box sx={{ display: 'grid', gap: '12px', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', ...fadeWhenStale }} data-testid="agent-metrics-tiles">
                    <StatTile
                        label="Conversations opened"
                        help="Conversations with new messages that agents opened in this range."
                        value={shown(current.opened)}
                        delta={tileDelta(current.opened, before?.opened)}
                        note={noteFor(before?.opened)}
                        {...tileProps}
                    />
                    <StatTile
                        label="Replies to guests"
                        help="Messages agents sent in reply to a guest, within 48 hours of the guest's message."
                        value={shown(current.replies)}
                        delta={tileDelta(current.replies, before?.replies)}
                        note={noteFor(before?.replies)}
                        {...tileProps}
                    />
                    <StatTile
                        label="Median response time"
                        help="Half of all replies to guests in this range came faster than this, timed from the guest's first unanswered message. Overnight waits move it far less than the average."
                        value={shown(current.medianSeconds, formatDuration)}
                        caption={reportLoaded && avgSeconds !== null && avgSeconds !== undefined ? `Average ${formatDuration(avgSeconds)}` : ''}
                        delta={tileDelta(current.medianSeconds, before?.medianSeconds, { lowerIsBetter: true })}
                        note={noteFor(before?.medianSeconds)}
                        {...tileProps}
                    />
                    <StatTile
                        label="No action taken"
                        help="Conversations an agent opened and then left: nobody replied, closed or marked them unread within an hour. The change compares the share of conversations opened."
                        value={shown(current.noAction)}
                        caption={reportLoaded && current.noActionShare !== null ? `${pct(current.noActionShare)} of conversations opened` : ''}
                        delta={shareDelta}
                        note={noteFor(before?.noActionShare)}
                        {...tileProps}
                    />
                </Box>
                <TrendChart
                    points={trendPoints}
                    timezone={tz}
                    status={byDay ? 'ready' : (trendForThisRange ? trendState.status : 'loading')}
                    unavailableReason={unavailableReason}
                    dim={!byDay && trendPoints.length > 0 && (!trendForThisRange || trendState.status === 'loading')}
                />
            </Box>
            <Box sx={{ mt: '12px', ...fadeWhenStale }}>
                <AgentComparison rows={rows} byDay={byDay} reportLoaded={reportLoaded} />
            </Box>
        </Box>
    );
};

export default AgentMetricsSummary;
export { deltaFor, withRange };
